import { makeAutoObservable } from 'mobx'
import dayjs from 'dayjs'
import { restClient } from '@/Core'
import type { RatingRow } from '../Contracts/RatingRow'
import type { Notify } from '@/Ship/Contracts/Notify'

/**
 * Local store of `CompleteTaskModal`.
 * Owns rating rows (per-day rating + per-row comment), submission flag,
 * and the ratings+complete-status workflow.
 * Instantiated per modal mount via `useState(() => new CompleteTaskModalStore())`.
 */
export class CompleteTaskModalStore {
    rows: RatingRow[] = []
    loading = false

    constructor() {
        makeAutoObservable(this)
    }

    setRows(rows: RatingRow[]): void {
        this.rows = rows
    }

    updateRow(i: number, patch: Partial<RatingRow>): void {
        this.rows = this.rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r))
    }

    addRow(): void {
        this.rows = [...this.rows, { date: dayjs().startOf('day'), rating: 5, comment: '' }]
    }

    removeRow(i: number): void {
        this.rows = this.rows.filter((_, idx) => idx !== i)
    }

    setLoading(v: boolean): void {
        this.loading = v
    }

    /**
     * Builds initial per-day rating rows from `plannedLoadingDate` to today (inclusive).
     * Falls back to a single today-row if planned date is in the future or absent.
     */
    initFromAssignment(assignment: any): void {
        const today = dayjs().startOf('day')
        const planned = assignment?.plannedLoadingDate ? dayjs(assignment.plannedLoadingDate).startOf('day') : today
        const start = planned.isBefore(today) ? planned : today
        const rows: RatingRow[] = []
        let cur = start
        while (!cur.isAfter(today)) {
            rows.push({ date: cur, rating: 5, comment: '' })
            cur = cur.add(1, 'day')
        }
        this.setRows(rows.length ? rows : [{ date: today, rating: 5, comment: '' }])
    }

    async submit(assignmentUuid: string, notify: Notify): Promise<boolean> {
        this.setLoading(true)
        try {
            for (const row of this.rows) {
                await restClient.post(`/services/executor-assignments/${assignmentUuid}/ratings`, {
                    json: {
                        ratingDate: row.date.format('YYYY-MM-DD'),
                        rating: row.rating,
                        comment: row.comment || null,
                    },
                })
            }
            await restClient.post(`/services/executor-assignments/${assignmentUuid}/change-status`, {
                json: { status: 'completed_by_driver' },
            })
            notify.success('Завдання завершено')
            return true
        } catch {
            notify.error('Помилка завершення завдання')
            return false
        } finally {
            this.setLoading(false)
        }
    }
}
